import React, { useState, useMemo } from 'react';
import { Search, Filter, RefreshCw, X, FileText } from 'lucide-react';
import PCETable from './PCETable';
import PCEDetailModal from './PCEDetailModal';

const PCEList = ({ pces, onRefresh, onViewConsumption }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('created_at');
    const [refreshing, setRefreshing] = useState(false);
    const [viewingPCE, setViewingPCE] = useState(null);
    
    const [columnFilters, setColumnFilters] = useState({
        company_name: '',
        site_name: '',
        supplier_name: '',
        pce_number: '',
        car_kwh: '',
        contract_end_date: ''
    });

    const handleRefresh = async () => {
        setRefreshing(true);
        await onRefresh();
        setTimeout(() => setRefreshing(false), 1000);
    };

    const handleColumnFilterChange = (columnName, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [columnName]: value
        }));
    };

    const resetAllFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setColumnFilters({
            company_name: '',
            site_name: '',
            supplier_name: '',
            pce_number: '',
            car_kwh: '',
            contract_end_date: ''
        });
    };

    const filteredPCEs = useMemo(() => {
        return pces
            .filter(pce => {
                const lowercasedTerm = searchTerm.toLowerCase();
                const matchesSearch = 
                    (pce.site_name && pce.site_name.toLowerCase().includes(lowercasedTerm)) ||
                    (pce.pce_number && pce.pce_number.includes(lowercasedTerm)) ||
                    (pce.address?.city && pce.address.city.toLowerCase().includes(lowercasedTerm)) ||
                    (pce.company_name && pce.company_name.toLowerCase().includes(lowercasedTerm));
                
                const matchesFilter = filterStatus === 'all' || pce.connection_status === filterStatus;
                
                const matchesCompanyFilter = !columnFilters.company_name || 
                    (pce.company_name && pce.company_name.toLowerCase().includes(columnFilters.company_name.toLowerCase()));
                
                const matchesSiteFilter = !columnFilters.site_name || 
                    (pce.site_name && pce.site_name.toLowerCase().includes(columnFilters.site_name.toLowerCase())) ||
                    (pce.pce_number && pce.pce_number.includes(columnFilters.site_name));
                
                const matchesSupplierFilter = !columnFilters.supplier_name || 
                    (pce.supplier_name && pce.supplier_name.toLowerCase().includes(columnFilters.supplier_name.toLowerCase()));
                
                const matchesCarFilter = !columnFilters.car_kwh || 
                    (pce.car_kwh && pce.car_kwh.toString().includes(columnFilters.car_kwh)) ||
                    (pce.tarif_gaz_code && pce.tarif_gaz_code.toLowerCase().includes(columnFilters.car_kwh.toLowerCase()));
                
                const matchesDateFilter = !columnFilters.contract_end_date || 
                    (pce.contract_end_date && pce.contract_end_date.includes(columnFilters.contract_end_date));
                
                return matchesSearch && matchesFilter && matchesCompanyFilter && 
                       matchesSiteFilter && matchesSupplierFilter && matchesCarFilter && matchesDateFilter;
            })
            .sort((a, b) => {
                switch (sortBy) {
                    case 'site_name':
                        return a.site_name.localeCompare(b.site_name);
                    case 'status':
                        return a.connection_status.localeCompare(b.connection_status);
                    case 'flow':
                        return (b.contract_flow || 0) - (a.contract_flow || 0);
                    case 'created_at':
                    default:
                        return new Date(b.created_at) - new Date(a.created_at);
                }
            });
    }, [pces, searchTerm, filterStatus, sortBy, columnFilters]);

    const handleViewDetails = (pce) => {
        setViewingPCE(pce);
    };

    const getStatusCount = (status) => {
        if (status === 'all') return pces.length;
        return pces.filter(pce => pce.connection_status === status).length;
    };

    const hasActiveFilters = searchTerm || filterStatus !== 'all' || Object.values(columnFilters).some(value => value && value.trim() !== '');

    if (pces.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow border p-6 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Search className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="text-xs font-semibold text-gray-900 mb-2">
                    Aucun PCE configuré
                </h3>
                <p className="text-gray-600 text-xs">
                    Aucun Point de Comptage et d'Estimation n'a été trouvé
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filtres */}
            <div className="bg-white rounded-lg shadow border p-4">
                <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
                    <div className="relative flex-1 max-w-md">
                        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Rechercher par nom, PCE ou ville..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-xs"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Filter className="h-4 w-4 text-gray-400" />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                <option value="all">Tous ({getStatusCount('all')})</option>
                                <option value="connected">Connectés ({getStatusCount('connected')})</option>
                                <option value="not_connected">Non connectés ({getStatusCount('not_connected')})</option>
                                <option value="token_expired">Token expiré ({getStatusCount('token_expired')})</option>
                                <option value="error">Erreur ({getStatusCount('error')})</option>
                            </select>
                        </div>

                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <option value="created_at">Date de création</option>
                            <option value="site_name">Nom du site</option>
                            <option value="status">Statut</option>
                            <option value="flow">Débit</option>
                        </select>

                        {hasActiveFilters && (
                            <button
                                onClick={resetAllFilters}
                                className="flex items-center gap-2 px-3 py-1.5 text-red-600 border border-red-300 rounded-md hover:bg-red-50 text-xs transition-colors"
                                title="Réinitialiser tous les filtres"
                            >
                                <X className="h-3 w-3" />
                                Réinitialiser
                            </button>
                        )}

                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="flex items-center gap-2 px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-xs transition-colors"
                        >
                            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                            Actualiser
                        </button>
                    </div>
                </div>

                {/* Statistiques */}
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        {getStatusCount('connected')} connectés
                    </span>
                    <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        {getStatusCount('not_connected')} en attente
                    </span>
                    <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        {getStatusCount('error')} en erreur
                    </span>
                    {hasActiveFilters && (
                        <span className="flex items-center gap-1 text-green-600 font-medium">
                            <Filter className="h-3 w-3" />
                            {filteredPCEs.length} résultat{filteredPCEs.length > 1 ? 's' : ''} filtré{filteredPCEs.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow border overflow-hidden">
                <PCETable
                    pces={filteredPCEs}
                    onView={handleViewDetails}
                    filters={columnFilters}
                    onFilterChange={handleColumnFilterChange}
                />
            </div>

            {/* Modal */}
            {viewingPCE && (
                <PCEDetailModal
                    pce={viewingPCE}
                    onClose={() => setViewingPCE(null)}
                    onViewConsumption={onViewConsumption}
                />
            )}
        </div>
    );
}; 

export default PCEList;
